'use client'

import {
  AUTH_COOKIE_LOGGED_OUT_KEY,
  AUTH_COOKIE_PASSWORD_CHANGED_KEY,
  AUTH_COOKIE_SESSION_EXPIRED_KEY,
} from '@/feature/auth/services/auth.constant'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { useToast } from '@/ui/toast/Toast'
import { deleteCookie, useReactiveGetCookie } from 'cookies-next/client'
import { useEffect, type PropsWithChildren } from 'react'
import { pushAuthenticationTrackingEvent } from '../../services/account.tracking'

/**
 * Authentication component that handles session expiration and logout notifications.
 *
 * This component monitors cookies to show appropriate toasts:
 * - 'authenticated' cookie === 'false': session expired
 * - 'logged-out' cookie === 'true': voluntary logout
 * - 'password-changed' cookie === 'true': password just changed (set during login redirect)
 *
 * @component
 * @param {PropsWithChildren} props - React children components to be wrapped
 * @param {ReactNode} props.children - Child components to render
 */
export const Authentication = ({ children }: PropsWithChildren) => {
  const toast = useToast()
  const isAuthenticated = useIsAuthenticated()
  const isHydrated = useNextIsClientHydrated()
  const router = useRouter()
  const { t } = useTranslation(['authentication', 'account'])

  const getCookie = useReactiveGetCookie()
  const authenticatedCookie = getCookie(AUTH_COOKIE_SESSION_EXPIRED_KEY)
  const loggedOutCookie = getCookie(AUTH_COOKIE_LOGGED_OUT_KEY)
  const passwordChangedCookie = getCookie(AUTH_COOKIE_PASSWORD_CHANGED_KEY)

  // Detect session ended on another tab: layout thinks we're authenticated but cookie says otherwise.
  // router.refresh() re-fetches Server Components so the layout updates to guest mode.
  useEffect(() => {
    if (isHydrated && isAuthenticated && authenticatedCookie !== 'true') {
      router.refresh()
    }
  }, [isHydrated, isAuthenticated, authenticatedCookie, router])

  useEffect(() => {
    if (authenticatedCookie === 'false' && !isAuthenticated) {
      toast.info({
        title: t('authentication:toast.expired.title'),
        message: t('authentication:toast.expired.message'),
      })

      pushAuthenticationTrackingEvent({
        action: 'déconnexion compte',
        label: 'session expirée',
      })

      deleteCookie(AUTH_COOKIE_SESSION_EXPIRED_KEY, { path: '/' })
    }
  }, [t, authenticatedCookie, isAuthenticated, toast])

  useEffect(() => {
    if (loggedOutCookie === 'true' && !isAuthenticated) {
      toast.success({
        message: t('account:toast.signout.message'),
      })

      deleteCookie(AUTH_COOKIE_LOGGED_OUT_KEY, { path: '/' })
    }
  }, [t, loggedOutCookie, isAuthenticated, toast])

  useEffect(() => {
    if (passwordChangedCookie === 'true') {
      toast.success({
        message: t('authentication:toast.passwordChanged.message'),
      })

      deleteCookie(AUTH_COOKIE_PASSWORD_CHANGED_KEY, { path: '/' })
    }
  }, [t, passwordChangedCookie, toast])

  return children
}
